import React from 'react'
import clsx from 'clsx'
import { ClickAwayListener, Popper } from '@mui/material'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'

import DownArrowSmallIcon from '../../icons/DownArrowSmallIcon'
import Folder2Icon from '../../icons/Folder2Icon'

import Search from '../Search'

import {
  resetFolderSearch,
  setFolderSearchVal,
  createNewFolder,
  fetchArticle
} from '../../../slices/article'
import useStyles from './styles'

const CREATE_VALUE = 'CREATE'

const FolderSelectMenu = ({ value, onSelect }) => {
  const dispatch = useAppDispatch()
  const {
    folderSearchVal,
    folderSearchResults,
    folderSearching,
    knownFolders,
    newFolderName,
    articles
  } = useAppSelector(state => state.article)

  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = React.useMemo(() => Boolean(anchorEl), [anchorEl])
  const mainRef = React.useRef()
  const [parentTitle, setParentTitle] = React.useState('')
  const searchResults = React.useMemo(
    () => (folderSearchResults ?? knownFolders).slice(0, 5),
    [folderSearchResults, knownFolders]
  )

  const classes = useStyles()

  // Determines name of parent
  React.useEffect(() => {
    if (!value) {
      setParentTitle('Unassigned')
      return
    }

    if (!articles[value]) {
      dispatch(fetchArticle(value))
      return
    }

    setParentTitle(articles[value].title)
  }, [value, articles, dispatch])

  // Opens menu
  const handleClick = React.useCallback(e => {
    const target = e.currentTarget
    setAnchorEl(old => (old ? null : target))
  }, [])

  // Closes menu and resets state
  const handleClose = React.useCallback(() => {
    setAnchorEl(null)
    dispatch(resetFolderSearch())
  }, [dispatch])

  // Callback for when something is clicked
  const handleSelect = React.useCallback(
    id => {
      if (id === CREATE_VALUE) {
        dispatch(createNewFolder())
        onSelect && onSelect(null)
      } else {
        onSelect && onSelect(id)
      }

      handleClose()
    },
    [dispatch, onSelect, handleClose]
  )

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        <div ref={mainRef} onClick={handleClick} className={classes.root}>
          <div className={classes.selectedFolder}>
            {newFolderName ?? parentTitle}
          </div>

          <DownArrowSmallIcon />
        </div>

        {open && (
          <Popper
            open={open}
            anchorEl={anchorEl}
            style={{ zIndex: '1500' }}
            placement='bottom-start'
          >
            <div className={classes.menu}>
              <Search
                placeholder='Type to search or create new folder ...'
                value={folderSearchVal}
                onChange={e => dispatch(setFolderSearchVal(e.target.value))}
                maxLength={20}
                loading={folderSearching}
                className={classes.search}
              />

              {folderSearchVal.length > 0 && (
                <div
                  onClick={() => handleSelect(CREATE_VALUE)}
                  className={clsx(classes.item, classes.createItem)}
                >
                  <Folder2Icon className={classes.createIcon} />
                  Create folder: "{folderSearchVal}"
                </div>
              )}

              <div className={classes.items}>
                {searchResults.map(res => (
                  <div
                    key={res.id}
                    onClick={() => handleSelect(res.id)}
                    className={classes.item}
                  >
                    {res.title}
                  </div>
                ))}
              </div>
            </div>
          </Popper>
        )}
      </div>
    </ClickAwayListener>
  )
}

export default FolderSelectMenu
